<template>
  <div class="webdav-container">
    <div class="webdav-content">
      <!-- 依赖包信息 -->
      <el-card class="info-card">
        <template #header>
          <div class="card-header">
            <span>{{ $t('mount.dependencyPackage') }}</span>
          </div>
        </template>

        <el-table :data="[packageInfo]" border>
          <el-table-column prop="package" :label="$t('mount.packageInfo')" width="120" />
          <el-table-column prop="system" :label="$t('mount.systemInfo')" width="240" />
          <el-table-column prop="status" :label="$t('mount.status')">
            <template #default="{ row }">
              <span v-if="osType !== 'windows'">{{ row.status }}</span>
              <div v-else class="action-links">
                <el-link type="primary" href="https://winfsp.dev/rel/" target="_blank">{{ $t('mount.winfspDownload') }}</el-link>
                <el-link type="primary" @click="showWIPreview = true">
                  {{ $t('mount.operationGuide') }}
                </el-link>
                <el-image-viewer
                  v-if="showWIPreview"
                  :url-list="[imageWIUrl]"
                  :initial-index="0"
                  @close="showWIPreview = false"
                />
              </div>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <!-- 挂载列表 -->
      <el-card class="mount-list-card">
        <template #header>
          <div class="card-header">
            <span>{{ $t('mount.mountList') }}</span>
            <div class="header-actions">
              <el-button type="info" size="small" @click="showModuleLogDialog">
                <el-icon><Document /></el-icon>
                {{ $t('mount.viewLog') }}
              </el-button>
              <el-button type="primary" size="small" @click="showAddDialog">{{ $t('mount.add') }}</el-button>
              <el-link type="primary" @click="showMCPreview = true">
                {{ $t('mount.operationGuide') }}
              </el-link>
              <el-image-viewer
                v-if="showMCPreview"
                :url-list="[imageMCUrl]"
                :initial-index="0"
                @close="showMCPreview = false"
              />
            </div>
          </div>
          <span class="card-subtitle"  v-if="!isExeAdmin">
            {{ $t('mount.runningAsNonAdmin') }}
            <span class="text-error">{{ $t('mount.runningAsNonAdminNote') }}</span>
          </span>
          <span class="card-subtitle"  v-else>
            {{ $t('mount.runningAsAdmin') }}
          </span>
        </template>

        <!-- 当 mountList 为空时显示提示 -->
        <div v-if="mountList == null || mountList.length === 0" class="empty-mount-list">
          <el-empty :description="$t('mount.noMountList')" />
        </div>

        <div class="mount-items-container">
          <div v-for="(row, index) in mountList" :key="index" class="mount-item">
            <div class="mount-item-header">
              <div class="mount-name-status">
                <span class="mount-name">{{ row.Name }}</span>
                <span
                  class="mount-status"
                  :class="{
                    'status-success': row.Switch && row.Status?.includes('成功'),
                    'status-inactive': !row.Switch,
                    'status-error': row.Switch && !row.Status?.includes('成功')
                  }"
                >
                  {{ row.Status }}
                </span>
              </div>
              <div class="mount-actions">
                <!-- 当 row.Switch 为 true 且 Status 包含 "成功" 时，显示 "停止" 和 "打开" -->
                <template v-if="row.Switch && row.Status?.includes('成功')">
                  <el-button
                    type="primary"
                    size="small"
                    @click="stopWebdavClient(row)"
                  >
                    {{ $t('mount.stop') }}
                  </el-button>
                  <el-button
                    type="success"
                    size="small"
                    @click="openMount(row)"
                  >
                    {{ $t('mount.open') }}
                  </el-button>
                  <el-button
                    type="info"
                    size="small"
                    @click="viewLog(row)"
                  >
                    {{ $t('mount.log') }}
                  </el-button>
                </template>

                <!-- 否则（row.Switch 为 false 或 Status 不包含 "成功"），显示 "启动"、"编辑"、"删除" -->
                <template v-else>
                  <el-button
                    type="primary"
                    size="small"
                    @click="startWebdavClient(row)"
                  >
                    {{ $t('mount.start') }}
                  </el-button>
                  <el-button
                    type="warning"
                    size="small"
                    @click="editWebdavClient(row)"
                  >
                    {{ $t('mount.edit') }}
                  </el-button>
                  <el-button
                    type="danger"
                    size="small"
                    @click="deleteWebdavClient(row)"
                  >
                    {{ $t('mount.delete') }}
                  </el-button>
                  <el-button
                    type="info"
                    size="small"
                    @click="viewLog(row)"
                  >
                    {{ $t('mount.log') }}
                  </el-button>
                </template>
              </div>
            </div>

            <div class="mount-details">
              <div class="mount-info-row">
                <span class="mount-info-text">【{{ row.MountPoint }}】</span>
                <span class="mount-info-text">{{ row.MountName || $t('mount.unmounted') }}</span>
                <span class="mount-info-text">{{ row.RemoteAddr }}</span>
                <span class="mount-info-text">{{ row.RemoteUsername }}</span>
                <span class="mount-info-text">{{ row.MountVender }}</span>
                <span class="mount-info-text">{{ row.MountMode === 'netDisk' ? $t('mount.networkDrive') : $t('mount.localDrive') }}</span>
              </div>
            </div>

            <div class="mount-storage">
              <el-progress
                :percentage="calculateUsage(row.UserVolume, row.TotalVolume)"
                :color="getProgressColor(row.UserVolume, row.TotalVolume)"
                :stroke-width="16"
                :format="formatProgressText(row.UserVolume, row.TotalVolume)"
              />
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 新增挂载对话框 -->
    <el-dialog v-model="addDialogVisible" :title="$t('mount.addMount')" :width="isMobile ? '100%' : '800px'">
      <el-form :model="newMount" :rules="mountRules" label-width="120px">
        <el-form-item :label="$t('mount.name')" prop="Name">
          <el-input v-model="newMount.Name" :placeholder="$t('mount.namePlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.mountName')">
          <el-input v-model="newMount.MountName" :placeholder="$t('mount.mountNamePlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.mountPoint')" prop="MountPoint">
          <el-input v-model="newMount.MountPoint" :placeholder="$t('mount.mountPointPlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.webdavAddress')" prop="RemoteAddr">
          <el-input v-model="newMount.RemoteAddr" :placeholder="$t('mount.webdavAddressPlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.username')" prop="RemoteUsername">
          <el-input v-model="newMount.RemoteUsername" :placeholder="$t('mount.usernamePlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.password')" prop="RemotePassword">
          <el-input
            v-model="newMount.RemotePassword"
            type="password"
            :placeholder="$t('mount.passwordPlaceholder')"
            show-password
          />
        </el-form-item>

        <el-form-item :label="$t('mount.method')" prop="MountVender">
          <el-select
            v-model="newMount.MountVender"
            style="width: 100%"
            :disabled="osType !== 'windows'"
          >
            <el-option :label="$t('mount.tfcenter')" value="tfcenter" />
            <el-option :label="$t('mount.rclone')" value="rclone" />
          </el-select>
          <span v-if="osType !== 'windows'" class="disabled-hint">
            {{ $t('mount.linuxOnlyRclone') }}
          </span>
        </el-form-item>

        <el-form-item :label="$t('mount.mode')" prop="MountMode">
          <el-select v-model="newMount.MountMode" style="width: 100%">
            <el-option :label="$t('mount.networkDrive')" value="netDisk" />
            <el-option :label="$t('mount.localDrive')" value="localDisk" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="addDialogVisible = false">{{ $t('mount.cancel') }}</el-button>
        <el-button type="primary" @click="addWebdavClient">{{ $t('mount.save') }}</el-button>
      </template>
    </el-dialog>

    <!-- 编辑挂载对话框 -->
    <el-dialog v-model="editDialogVisible" :width="isMobile ? '100%' : '800px'">
      <template #header>
        <span>{{ $t('mount.editMount') }}</span>
      </template>
      <el-form :model="editMount" :rules="editMountRules" label-width="120px">
        <el-form-item :label="$t('mount.name')" prop="Name">
          <el-input v-model="editMount.Name" readonly class="readonly-input" disabled />
        </el-form-item>

        <el-form-item :label="$t('mount.mountName')">
          <el-input v-model="editMount.MountName" :placeholder="$t('mount.mountNamePlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.mountPoint')" prop="MountPoint">
          <el-input v-model="editMount.MountPoint" :placeholder="$t('mount.mountPointPlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.webdavAddress')" prop="RemoteAddr">
          <el-input v-model="editMount.RemoteAddr" :placeholder="$t('mount.webdavAddressPlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.username')" prop="RemoteUsername">
          <el-input v-model="editMount.RemoteUsername" :placeholder="$t('mount.usernamePlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('mount.password')">
          <el-input
            v-model="editMount.RemotePassword"
            type="password"
            :placeholder="$t('mount.leaveEmptyToKeepPassword')"
            show-password
          />
        </el-form-item>

        <el-form-item :label="$t('mount.method')" prop="MountVender">
          <el-select
            v-model="editMount.MountVender"
            style="width: 100%"
            :disabled="osType !== 'windows'"
          >
            <el-option :label="$t('mount.tfcenter')" value="tfcenter" />
            <el-option :label="$t('mount.rclone')" value="rclone" />
          </el-select>
          <span v-if="osType !== 'windows'" class="disabled-hint">
            {{ $t('mount.linuxOnlyRclone') }}
          </span>
        </el-form-item>

        <el-form-item :label="$t('mount.mode')" prop="MountMode">
          <el-select v-model="editMount.MountMode" style="width: 100%">
            <el-option :label="$t('mount.networkDrive')" value="netDisk" />
            <el-option :label="$t('mount.localDrive')" value="localDisk" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="editDialogVisible = false">{{ $t('mount.cancel') }}</el-button>
        <el-button type="primary" @click="updateWebdavClient">{{ $t('mount.save') }}</el-button>
      </template>
    </el-dialog>

    <!-- 单个挂载点日志查看对话框 -->
    <el-dialog v-model="logDialogVisible" :title="$t('mount.logContent') + currentLogName" :width="isMobile ? '100%' : '800px'">
      <div class="log-container">
        <div class="log-toolbar" v-if="logContent">
          <div class="log-stats">
            <span>{{ $t('mount.logSize') }} {{ formatLogSize(logContent.length) }}</span>
          </div>
          <div class="log-actions">
            <el-button
              type="primary"
              size="small"
              @click="refreshLog"
              :icon="Refresh"
            >
              {{ $t('mount.refreshLog') }}
            </el-button>
            <el-button
              type="success"
              size="small"
              @click="downloadCurrentLog"
              :icon="Download"
            >
              {{ $t('mount.downloadLog') }}
            </el-button>
          </div>
        </div>
        <pre class="log-pre">{{ logContent || $t('mount.noLogContent') }}</pre>
      </div>
      <template #footer>
        <el-button @click="logDialogVisible = false">{{ $t('mount.close') }}</el-button>
      </template>
    </el-dialog>

    <!-- WebDAV模块日志查看对话框 -->
    <el-dialog
      v-model="moduleLogDialogVisible"
      :title="$t('mount.runningLog')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('mount.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('mount.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('mount.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('mount.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshModuleLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('mount.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('mount.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('mount.noLogRecords') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="moduleLogDialogVisible = false">{{ $t('mount.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Document, Refresh, Download, InfoFilled } from '@element-plus/icons-vue'
import { API_BASE_URL } from '@/config';
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const router = useRouter()

// 图片路径
const imageMCUrl = '/static/dist/images/mount-config.png';
const showMCPreview = ref(false);
const imageWIUrl = '/static/dist/images/winfsp-install.png';
const showWIPreview = ref(false);

// 系统信息
const osType = ref('windows')
const platform = ref('amd64')
const isExeAdmin = ref(false)

// 日志查看相关
const logDialogVisible = ref(false)
const logContent = ref('')
const currentLogName = ref('')

// 模块日志相关
const moduleLogDialogVisible = ref(false)
const logLoading = ref(false)
const logClearing = ref(false)
const logDownloading = ref(false)
const logsData = ref([])
const logTotal = ref(0)
const logCurrentPage = ref(1)
const logPageSize = ref(50)
const logContentRef = ref(null)
const autoRefreshEnabled = ref(true)
const logRefreshInterval = ref(null)

const isMobile = window.innerWidth < 768;

// 依赖包信息
const packageInfo = reactive({
  package: 'winfsp',
  system: computed(() => `${osType.value}-${platform.value}`),
  status: '已安装'
})

// 挂载列表
const mountList = ref([])

// 新增挂载对话框
const addDialogVisible = ref(false)
const newMount = reactive({
  Name: '',
  MountName: '',
  MountPoint: '',
  RemoteAddr: '',
  RemoteUsername: '',
  RemotePassword: '',
  MountVender: 'tfcenter',
  MountMode: 'netDisk',
  MountType: 'webdav',
  CacheMode: 'full',
  RefreshTime: '10'
})

// 编辑挂载对话框
const editDialogVisible = ref(false)
const editMount = reactive({
  Name: '',
  MountName: '',
  MountPoint: '',
  RemoteAddr: '',
  RemoteUsername: '',
  RemotePassword: '',
  MountVender: 'tfcenter',
  MountMode: 'netDisk'
})

// 新增表单验证规则
const mountRules = {
  Name: [
    { required: true, message: t('mount.nameRequired'), trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9]{3,15}$/, message: t('mount.nameFormatError'), trigger: 'blur' }
  ],
  MountPoint: [
    { required: true, message: t('mount.mountPointRequired'), trigger: 'blur' }
  ],
  RemoteAddr: [
    { required: true, message: t('mount.webdavAddressRequired'), trigger: 'blur' }
  ],
  RemoteUsername: [
    { required: true, message: t('mount.usernameRequired'), trigger: 'blur' }
  ],
  RemotePassword: [
    { required: true, message: t('mount.passwordRequired'), trigger: 'blur' },
    { min: 2, max: 64, message: t('mount.passwordLengthError'), trigger: 'blur' }
  ],
  MountVender: [
    { required: true, message: t('mount.pleaseSelectMethod'), trigger: 'change' }
  ],
  MountMode: [
    { required: true, message: t('mount.pleaseSelectMode'), trigger: 'change' }
  ]
}

// 编辑表单验证规则
const editMountRules = {
  MountPoint: [
    { required: true, message: t('mount.mountPointRequired'), trigger: 'blur' }
  ],
  RemoteAddr: [
    { required: true, message: t('mount.webdavAddressRequired'), trigger: 'blur' }
  ],
  RemoteUsername: [
    { required: true, message: t('mount.usernameRequired'), trigger: 'blur' }
  ],
  MountVender: [
    { required: true, message: t('mount.pleaseSelectMethod'), trigger: 'change' }
  ],
  MountMode: [
    { required: true, message: t('mount.pleaseSelectMode'), trigger: 'change' }
  ]
}

// 检查登录状态
const checkLogin = (data) => {
  if (data?.errCode === 1000) {
    ElMessage.warning(t('mount.pleaseLoginFirst'))
    router.push('/user-login?callback=webdav-mgmt')
    return false
  }
  return true
}

// 格式化日志大小
const formatLogSize = (bytes) => {
  if (!bytes) return '0B'
  const num = parseFloat(bytes)
  if (isNaN(num)) return '0B'
  
  if (num >= 1024 * 1024) {
    return (num / (1024 * 1024)).toFixed(2) + 'MB'
  } else if (num >= 1024) {
    return (num / 1024).toFixed(2) + 'KB'
  }
  return num + 'B'
}

// 查看单个挂载点日志
const viewLog = async (row) => {
  try {
    currentLogName.value = row.Name
    const response = await fetch(`${API_BASE_URL}/download-log-file?path=${row.Name}.txt`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(t('mount.fetchLogFailed'))
    }

    logContent.value = await response.text()
    logDialogVisible.value = true
  } catch (error) {
    ElMessage.error(t('mount.fetchLogContentFailed', { error: error.message }))
  }
}

// 刷新单个挂载点日志
const refreshLog = async () => {
  if (currentLogName.value) {
    await viewLog({ Name: currentLogName.value })
    ElMessage.success(t('mount.logRefreshed'))
  }
}

// 下载当前挂载点日志
const downloadCurrentLog = async () => {
  if (!currentLogName.value || !logContent.value) {
    ElMessage.warning(t('mount.noLogToDownload'))
    return
  }

  try {
    const blob = new Blob([logContent.value], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${currentLogName.value}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    ElMessage.success(t('mount.logDownloadSuccess'))
  } catch (error) {
    console.error(t('mount.logDownloadFailed', { error: error.message }), error)
    ElMessage.error(t('mount.logDownloadFailed', { error: error.message }))
  }
}

// 显示模块日志对话框
const showModuleLogDialog = () => {
  moduleLogDialogVisible.value = true
  fetchModuleLogs()
  if (autoRefreshEnabled.value) {
    startLogAutoRefresh()
  }
}

// 切换自动刷新
const toggleAutoRefresh = (enabled) => {
  if (enabled) {
    startLogAutoRefresh()
    ElMessage.success(t('mount.autoRefreshEnabled'))
  } else {
    stopLogAutoRefresh()
    ElMessage.info(t('mount.autoRefreshDisabled'))
  }
}

// 启动日志自动刷新
const startLogAutoRefresh = () => {
  stopLogAutoRefresh()
  logRefreshInterval.value = setInterval(() => {
    if (moduleLogDialogVisible.value) {
      fetchModuleLogs(false)
    }
  }, 5000)
}

// 停止日志自动刷新
const stopLogAutoRefresh = () => {
  if (logRefreshInterval.value) {
    clearInterval(logRefreshInterval.value)
    logRefreshInterval.value = null
  }
}

// 获取模块日志
const fetchModuleLogs = async (showLoading = true) => {
  try {
    if (showLoading) {
      logLoading.value = true
    }

    const params = new URLSearchParams({
      module: 'mount',
      page: logCurrentPage.value,
      size: logPageSize.value
    })

    const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
      credentials: 'include'
    })

    if (!response.ok) throw new Error(t('mount.networkError'))

    const data = await response.json()
    if (!checkLogin(data)) return

    if (data.errCode !== 0) {
      throw new Error(data.errMsg || t('mount.fetchLogFailed'))
    }

    logsData.value = data.data || []
    logTotal.value = data.count || 0

    if (showLoading && logContentRef.value) {
      logContentRef.value.scrollTop = 0
    }
  } catch (error) {
    console.error(t('mount.fetchLogFailed'), error)
    if (showLoading) {
      ElMessage.error(t('mount.fetchLogContentFailed', { error: error.message || t('mount.unknownError') }))
    }
  } finally {
    if (showLoading) {
      logLoading.value = false
    }
  }
}

// 刷新模块日志
const refreshModuleLogs = () => {
  fetchModuleLogs(true)
}

// 清空日志
const clearLogs = async () => {
  try {
    await ElMessageBox.confirm(t('mount.confirmClearLogs'), t('mount.warning'), {
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel'),
      type: 'warning'
    })

    logClearing.value = true

    const params = new URLSearchParams({
      module: 'mount'
    })

    const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (!response.ok) throw new Error(t('mount.networkError'))

    const data = await response.json()
    if (!checkLogin(data)) return

    if (data.errCode !== 0) {
      throw new Error(data.errMsg || t('mount.clearLogFailed'))
    }

    ElMessage.success(t('mount.logsCleared'))
    fetchModuleLogs(true)
  } catch (error) {
    if (error !== 'cancel') {
      console.error(t('mount.clearLogFailed'), error)
      ElMessage.error(t('mount.clearLogFailed', { error: error.message || t('mount.unknownError') }))
    }
  } finally {
    logClearing.value = false
  }
}

// 下载所有日志
const downloadAllLogs = async () => {
  try {
    logDownloading.value = true

    const params = new URLSearchParams({
      module: 'mount',
      page: 1,
      size: 10000
    })

    const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
      credentials: 'include'
    })

    if (!response.ok) throw new Error(t('mount.networkError'))

    const data = await response.json()
    if (!checkLogin(data)) return

    if (data.errCode !== 0) {
      throw new Error(data.errMsg || t('mount.logsDownloadFailed', { error: '' }))
    }

    const allLogs = data.data || []

    if (allLogs.length === 0) {
      ElMessage.warning(t('mount.noLogsToDownload'))
      return
    }

    let logContent = ''
    allLogs.forEach(log => {
      logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
    })

    const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `webdav_module_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    ElMessage.success(t('mount.allLogsDownloadSuccess', { count: allLogs.length }))
  } catch (error) {
    console.error(t('mount.logsDownloadFailed', { error: error.message }), error)
    ElMessage.error(t('mount.logsDownloadFailed', { error: error.message || t('mount.unknownError') }))
  } finally {
    logDownloading.value = false
  }
}

// 处理每页条数变化
const handleLogSizeChange = (size) => {
  logPageSize.value = size
  logCurrentPage.value = 1
  fetchModuleLogs(true)
}

// 处理页码变化
const handleLogPageChange = (page) => {
  logCurrentPage.value = page
  fetchModuleLogs(true)
}

// 格式化存储容量
const formatStorage = (value) => {
  if (!value) return '0B';
  const num = parseFloat(value);
  if (isNaN(num)) return '0B';
  
  if (num >= 1024 * 1024 * 1024) {
    return (num / (1024 * 1024 * 1024)).toFixed(2) + 'GB';
  } else if (num >= 1024 * 1024) {
    return (num / (1024 * 1024)).toFixed(2) + 'MB';
  } else if (num >= 1024) {
    return (num / 1024).toFixed(2) + 'KB';
  }
  return num.toFixed(2) + 'B';
};

// 计算使用率
const calculateUsage = (used, total) => {
  if (!used || !total) return 0;
  const usedNum = parseFloat(used);
  const totalNum = parseFloat(total);
  if (isNaN(usedNum) || isNaN(totalNum) || totalNum === 0) return 0;
  return Math.round((usedNum / totalNum) * 100);
};

// 获取进度条颜色
const getProgressColor = (used, total) => {
  const usage = calculateUsage(used, total);
  if (usage > 90) return '#f56c6c';
  if (usage > 70) return '#e6a23c';
  return '#67c23a';
};

// 进度条文本格式化
const formatProgressText = (used, total) => {
  return () => {
    const usage = calculateUsage(used, total);
    return `${formatStorage(used)} / ${formatStorage(total)} (${usage}%)`;
  };
};

// 显示新增对话框
const showAddDialog = () => {
  Object.assign(newMount, {
    Name: '',
    MountName: '',
    MountPoint: '',
    RemoteAddr: '',
    RemoteUsername: '',
    RemotePassword: '',
    MountVender: osType.value !== 'windows' ? 'rclone' : 'tfcenter',
    MountMode: 'netDisk',
    MountType: 'webdav',
    CacheMode: 'full',
    RefreshTime: '10'
  })
  addDialogVisible.value = true
}

// 添加WebDAV客户端
const addWebdavClient = async () => {
  try {
    const formData = new URLSearchParams();
    formData.append('name', newMount.Name);
    formData.append('mountName', newMount.MountName);
    formData.append('mountPoint', newMount.MountPoint);
    formData.append('remoteAddr', newMount.RemoteAddr);
    formData.append('remoteUsername', newMount.RemoteUsername);
    formData.append('remotePassword', newMount.RemotePassword);
    formData.append('mountVender', newMount.MountVender);
    formData.append('mountMode', newMount.MountMode);

    const response = await fetch(`${API_BASE_URL}/add-webdav-client`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
      credentials: 'include'
    })

    const result = await response.json()
    if (!checkLogin(result)) return

    if (result.errCode === 0) {
      ElMessage.success(result.errMsg)
      addDialogVisible.value = false
      fetchMountList()
    } else {
      ElMessage.error(result.errMsg)
    }
  } catch (error) {
    ElMessage.error(t('mount.addFailed') + (error.message || ''))
  }
}

// 启动WebDAV客户端
const startWebdavClient = async (row) => {
  try {
    const formData = new URLSearchParams();
    formData.append('name', row.Name);

    const response = await fetch(`${API_BASE_URL}/start-webdav-client`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
      credentials: 'include'
    })

    const result = await response.json()
    if (!checkLogin(result)) return

    if (result.errCode === 0) {
      ElMessage.success(result.errMsg)
      fetchMountList()
    } else {
      ElMessage.error(result.errMsg)
    }
  } catch (error) {
    ElMessage.error(t('mount.startFailed') + (error.message || ''))
  }
}

// 编辑WebDAV客户端
const editWebdavClient = (row) => {
  Object.assign(editMount, {
    Name: row.Name,
    MountName: row.MountName,
    MountPoint: row.MountPoint,
    RemoteAddr: row.RemoteAddr,
    RemoteUsername: row.RemoteUsername,
    RemotePassword: '', // 密码初始化为空
    MountVender: row.MountVender,
    MountMode: row.MountMode
  })
  editDialogVisible.value = true
}

// 更新WebDAV客户端
const updateWebdavClient = async () => {
  try {
    const formData = new URLSearchParams();
    formData.append('name', editMount.Name);
    formData.append('mountName', editMount.MountName);
    formData.append('mountPoint', editMount.MountPoint);
    formData.append('remoteAddr', editMount.RemoteAddr);
    formData.append('remoteUsername', editMount.RemoteUsername);

    // 只有密码不为空时才添加密码字段
    if (editMount.RemotePassword) {
      formData.append('remotePassword', editMount.RemotePassword);
    }

    formData.append('mountVender', editMount.MountVender);
    formData.append('mountMode', editMount.MountMode);

    const response = await fetch(`${API_BASE_URL}/update-webdav-client`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
      credentials: 'include'
    })

    const result = await response.json()
    if (!checkLogin(result)) return

    if (result.errCode === 0) {
      ElMessage.success(result.errMsg)
      editDialogVisible.value = false
      fetchMountList()
    } else {
      ElMessage.error(result.errMsg)
    }
  } catch (error) {
    ElMessage.error(t('mount.updateFailed') + (error.message || ''))
  }
}

// 删除WebDAV客户端
const deleteWebdavClient = async (row) => {
  try {
    await ElMessageBox.confirm(t('mount.confirmDeleteMount', { name: row.Name }), t('mount.warning'), {
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel'),
      type: 'warning'
    })

    const response = await fetch(
      `${API_BASE_URL}/delete-webdav-client?name=${encodeURIComponent(row.Name)}`,
      {
        method: 'DELETE',
        credentials: 'include'
      }
    );

    const result = await response.json()
    if (!checkLogin(result)) return

    if (result.errCode === 0) {
      ElMessage.success(result.errMsg)
      fetchMountList()
    } else {
      ElMessage.error(result.errMsg)
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(t('mount.deleteFailed') + (error.message || ''))
    }
  }
}

// 停止WebDAV客户端
const stopWebdavClient = async (row) => {
  try {
    const formData = new URLSearchParams();
    formData.append('name', row.Name);

    const response = await fetch(`${API_BASE_URL}/stop-webdav-client`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
      credentials: 'include'
    })

    const result = await response.json()
    if (!checkLogin(result)) return

    if (result.errCode === 0) {
      ElMessage.success(result.errMsg)
      fetchMountList()
    } else {
      ElMessage.error(result.errMsg)
    }
  } catch (error) {
    ElMessage.error(t('mount.stopFailed') + (error.message || ''))
  }
}

const openMount = async (row) => {
  try {
    await ElMessageBox.confirm(`${t('mount.cannotOpenFolderManually')}
          ${row.MountPoint}
          ${t('mount.clickConfirmToCopyPath')}`, t('mount.warning'), {
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel'),
      type: 'warning'
    })

    // 创建一个虚拟元素来安全复制
    const copy = (text) => {
      return new Promise((resolve) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';  // 避免触发布局变化
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        resolve();
      });
    };

    // 执行流程
    await copy(row.MountPoint);
  } catch (error) {
    // 用户点击取消或其他错误处理
    console.log(t('mount.copyCanceledOrFailed'), error);
  }
};

// 获取挂载列表
const fetchMountList = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/get-webdav-client`, {
      credentials: 'include'
    })
    const result = await response.json()
    if (!checkLogin(result)) return

    if (result.errCode === 0) {
      mountList.value = result.data
      osType.value = result.osType || 'windows'
      platform.value = result.platform || 'amd64'
      isExeAdmin.value = result.isExeAdmin
    } else {
      ElMessage.error(result.errMsg)
    }
  } catch (error) {
      ElMessage.error(t('mount.fetchMountListFailed', { error: error.message }))
  }
}

onMounted(() => {
  fetchMountList()
  startAutoRefresh()
})

// 自动刷新相关
const autoRefreshInterval = ref(null)

// 开始自动刷新
const startAutoRefresh = () => {
  fetchMountList() // 立即执行一次
  autoRefreshInterval.value = setInterval(fetchMountList, 5000) // 每5秒刷新一次
}

// 停止自动刷新
const stopAutoRefresh = () => {
  if (autoRefreshInterval.value) {
    clearInterval(autoRefreshInterval.value)
    autoRefreshInterval.value = null
  }
}

// 组件卸载时清除定时器
onUnmounted(() => {
  stopAutoRefresh()
  stopLogAutoRefresh()
})
</script>

<style scoped>
.webdav-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.webdav-content {
  margin: 0 auto;
}

.info-card,
.mount-list-card {
  margin-bottom: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.header-actions {
  margin-left:5px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.readonly-input {
  background-color: #f2f2f2;
  color: #808080;
}

.action-links {
  display: flex;
  gap: 10px;
}

/* 挂载项样式 */
.mount-items-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mount-item {
  background-color: #fff;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.mount-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.mount-name-status {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.mount-name {
  font-weight: bold;
  font-size: 16px;
}

.mount-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  background-color: #f2f2f2;
  color: #666;
}

.mount-status.status-success {
  background-color: #67c23a;
  color: white;
}

.mount-status.status-inactive {
  background-color: #909399;
  color: white;
}

.mount-status.status-error {
  background-color: #f56c6c;
  color: white;
}

.mount-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.mount-details {
  margin-bottom: 12px;
}

.mount-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
  align-items: center;
}

.mount-info-text {
  font-size: 13px;
  color: #606266;
  padding: 4px 8px;
  background-color: #f8f8f8;
  border-radius: 4px;
  line-height: 1.5;
}

.mount-storage {
  margin-top: 8px;
}

/* 进度条样式调整 */
:deep(.el-progress-bar) {
  padding-right: 0;
  margin-right: 0;
}

:deep(.el-progress__text) {
  font-size: 12px !important;
  color: #606266 !important;
  margin-left: 10px;
  min-width: 180px;
  font-family: monospace;
}

.card-subtitle {
  font-size: 14px;
  color: #606266;
  margin: 0;
  
  .text-error {
    color: #ff0000;
  }
}

/* 日志容器样式 */
.log-container {
  max-height: 60vh;
  overflow: auto;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
}

.log-pre {
  font-family: monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
  
  .log-stats {
    font-size: 12px;
    color: #606266;
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
}

/* 模块日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

/* Element Plus 对话框移动端适配 */
.settings-dialog {
  :deep(.el-dialog) {
    @media (max-width: 768px) {
      width: 95% !important;
      margin-top: 5vh !important;
    }
    
    @media (max-width: 480px) {
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      max-height: 100%;
      border-radius: 0;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      margin-bottom: 0 !important;
      
      .el-dialog__header {
        padding: 12px 16px;
        border-bottom: 1px solid #ebeef5;
      }
      
      .el-dialog__body {
        padding: 16px;
        max-height: calc(80vh - 120px);
        overflow-y: auto;
      }
      
      .el-dialog__footer {
        padding: 12px 16px;
        border-top: 1px solid #ebeef5;
      }
    }
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  @media (max-width: 768px) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-select) {
  @media (max-width: 768px) {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }
  
  @media (max-width: 480px) {
    .el-input__inner {
      height: 30px;
      line-height: 30px;
    }
  }
}

:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .mount-item-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .mount-actions {
    width: 100%;
    justify-content: flex-start;
  }
  
  .mount-info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  
  .mount-info-text {
    width: 100%;
    box-sizing: border-box;
  }
  
  :deep(.el-progress__text) {
    display: block;
    margin-top: 4px;
    margin-left: 0;
  }
}
</style>